/**
 * Première prise en main de la feuille de brouillon.
 *
 * Le didacticiel est l'exercice guidé de la démo (`GuidedTutorial`) : un vrai exercice de la classe, sur la feuille du
 * moteur v2, avec le coach qui montre où toucher. Il est proposé la PREMIÈRE fois qu'on prend le brouillon en main :
 * invité d'un défi (salon), fin de l'inscription, premier exercice. Une seule mémoire, sur l'appareil.
 *
 * Trois anciennes clés disent déjà « a vu le brouillon » : un utilisateur qui s'en est servi avant ce didacticiel
 * n'est pas renvoyé dessus.
 */

import type { SchoolLevel } from '@jeraisonne/db';
import { DEMO_LEVELS } from '@/features/demo/types';
import { demoExerciseFor } from '@/features/demo/demo-exercises';
import {
  markPaperTutorialSeen,
  markScratchTutorialSeen,
} from '@/features/scratch-sheet/sheet-tutorial';
import { markScratchTutorialDone } from './tutorial';

/** fini (page du tutoriel), bulles de l'exercice « sur la feuille », bulles de l'ancienne feuille */
export const SCRATCH_TUTORIAL_KEYS = [
  'jr_scratch_tutorial_done',
  'jr_paper_tuto_seen',
  'jr_scratch_tuto_seen',
] as const;

/**
 * Le brouillon n'a jamais été pris en main sur cet appareil ? Stockage inaccessible (navigation privée) : non — on ne
 * peut pas mémoriser qu'il est fait, on ne l'imposerait pas à chaque fois.
 */
export function isFirstScratchUse(): boolean {
  try {
    return SCRATCH_TUTORIAL_KEYS.every((k) => localStorage.getItem(k) !== '1');
  } catch {
    return false;
  }
}

/** Didacticiel fait ou passé : plus proposé, ni les anciennes bulles. */
export function markScratchTutorialFinished(): void {
  markScratchTutorialDone();
  markPaperTutorialSeen();
  markScratchTutorialSeen();
}

/** Classe du didacticiel : la sienne au collège et au lycée, Terminale en prépa, sinon 4e (Pythagore). PUR. */
export function tutorialLevelFor(level: string | null | undefined): SchoolLevel {
  if (level && (DEMO_LEVELS as string[]).includes(level)) return level as SchoolLevel;
  if (level === 'mpsi' || level === 'ptsi' || level === 'mp' || level === 'pt') return 'tle';
  return '4e';
}

/**
 * Exercice du didacticiel pour une classe. Jamais celui qu'on va jouer (`avoid`) : le didacticiel en donnerait la
 * réponse — on prend alors celui de la classe d'avant (ou d'après en 6e). PUR.
 */
export function tutorialExerciseFor(
  level: string | null | undefined,
  avoid?: string | null,
): string {
  const lv = tutorialLevelFor(level);
  const id = demoExerciseFor(lv);
  if (id && id !== avoid) return id;
  const i = DEMO_LEVELS.indexOf(lv);
  const other = DEMO_LEVELS[i > 0 ? i - 1 : i + 1]!;
  return demoExerciseFor(other)!;
}

/** Après le didacticiel de l'inscription : le tableau de bord, ou le diagnostic lancé. */
export type TutorialNext = 'tableau' | 'diagnostic';

/** Paramètres de `/brouillon/tutoriel` (`?niveau=4e&apres=diagnostic`). PUR. */
export function parseTutorialSearch(search: Record<string, unknown>): {
  niveau?: string;
  apres?: TutorialNext;
} {
  const niveau =
    typeof search.niveau === 'string' && /^[a-z0-9]{2,5}$/.test(search.niveau)
      ? search.niveau
      : undefined;
  const apres =
    search.apres === 'tableau' || search.apres === 'diagnostic' ? search.apres : undefined;
  return { niveau, apres };
}
